import { useState, useEffect, useRef } from 'react';
import { X, Clock, Smile } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export interface UserStatus {
  emoji: string;
  text: string;
  expiresAt: string | null;
}

interface StatusPickerProps {
  currentStatus: UserStatus | null;
  onStatusSet: (status: UserStatus | null) => void;
  onClose: () => void;
}

/** Status presets; the text is translated into the app language when picked. */
const PRESETS = [
  { emoji: '🏠', key: 'remote' },
  { emoji: '📅', key: 'meeting' },
  { emoji: '🏖️', key: 'vacation' },
  { emoji: '🔴', key: 'dnd' },
  { emoji: '🍽️', key: 'lunch' },
  { emoji: '🏃', key: 'brb' },
  { emoji: '🤒', key: 'sick' },
  { emoji: '🎉', key: 'celebrating' },
];

function presetText(t: TFunction, p: typeof PRESETS[0]): string {
  switch (p.key) {
    case 'remote': return t('uiKit.status.presets.remote', 'Working remotely');
    case 'meeting': return t('uiKit.status.presets.meeting', 'In a meeting');
    case 'vacation': return t('uiKit.status.presets.vacation', 'On vacation');
    case 'dnd': return t('uiKit.status.presets.dnd', 'Do not disturb');
    case 'lunch': return t('uiKit.status.presets.lunch', 'Out for lunch');
    case 'brb': return t('uiKit.status.presets.brb', 'Be right back');
    case 'sick': return t('uiKit.status.presets.sick', 'Out sick');
    default: return t('uiKit.status.presets.celebrating', 'Celebrating');
  }
}

const DURATION_OPTIONS = [
  { label: '30 minutes',   ms: 30 * 60_000 },
  { label: '1 hour',       ms: 60 * 60_000 },
  { label: '4 hours',      ms: 4 * 60 * 60_000 },
  { label: 'Today',        ms: 0, isToday: true },
  { label: 'This week',    ms: 0, isWeek: true },
  { label: "Don't clear",  ms: 0, noClear: true },
];

function durationLabel(t: TFunction, opt: typeof DURATION_OPTIONS[0]): string {
  switch (opt.label) {
    case '30 minutes': return t('uiKit.status.duration.min30', '30 minutes');
    case '1 hour': return t('uiKit.status.duration.hour1', '1 hour');
    case '4 hours': return t('uiKit.status.duration.hours4', '4 hours');
    case 'Today': return t('uiKit.status.duration.today', 'Today');
    case 'This week': return t('uiKit.status.duration.week', 'This week');
    default: return t('uiKit.status.duration.never', "Don't clear");
  }
}

function computeExpiry(opt: typeof DURATION_OPTIONS[0]): string | null {
  if (opt.noClear) return null;
  if (opt.isToday) {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }
  if (opt.isWeek) {
    const end = new Date();
    const dayOfWeek = end.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    end.setDate(end.getDate() + daysUntilSunday);
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }
  return new Date(Date.now() + opt.ms).toISOString();
}

export function StatusPicker({ currentStatus, onStatusSet, onClose }: StatusPickerProps) {
  const { t } = useTranslation();
  const [emoji, setEmoji] = useState(currentStatus?.emoji ?? '');
  const [text, setText] = useState(currentStatus?.text ?? '');
  const [durationIdx, setDurationIdx] = useState(5); // "Don't clear" default
  const [clearOnOffline, setClearOnOffline] = useState(() => localStorage.getItem('eurion_clear_status_offline') === 'true');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSave = async () => {
    if (!text.trim() && !emoji.trim()) return;
    setSaving(true);
    try {
      const expiresAt = computeExpiry(DURATION_OPTIONS[durationIdx]!);
      const res = await api.put<{ statusText: string; statusEmoji: string; statusExpiresAt: string | null }>('/v1/users/me/status', {
        emoji: emoji || undefined,
        text: text.trim() || undefined,
        expiresAt: expiresAt || undefined,
      });
      if (res.success) {
        onStatusSet({ emoji, text: text.trim(), expiresAt });
        // Send clear-on-offline preference via WebSocket
        try {
          const { usePresenceStore } = await import('../../stores/presenceStore');
          const ws = usePresenceStore.getState().ws;
          if (ws && ws.readyState === 1) {
            ws.send(JSON.stringify({ type: 'presence.clearonoffline', enabled: clearOnOffline }));
          }
          localStorage.setItem('eurion_clear_status_offline', String(clearOnOffline));
        } catch { /* non-critical */ }
      }
    } catch { /* silent */ }
    setSaving(false);
    onClose();
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.delete('/v1/users/me/status');
      onStatusSet(null);
    } catch { /* silent */ }
    setSaving(false);
    onClose();
  };

  const handlePreset = (preset: typeof PRESETS[0]) => {
    setEmoji(preset.emoji);
    setText(presetText(t, preset));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-sm rounded-xl border border-nord-3/40 bg-nord-1 shadow-2xl animate-modal-in"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-nord-3 px-4 py-3">
          <h2 className="text-sm font-semibold text-nord-6">{t('topbar.setStatus', 'Set a status')}</h2>
          <button onClick={onClose} aria-label={t('common.close', 'Close')} className="rounded p-1 text-nord-4 hover:text-nord-6 hover:bg-nord-2 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Emoji + Text Input */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 focus-within:border-frost-2 transition-colors">
            <span className="text-lg select-none">{emoji || <Smile className="h-5 w-5 text-nord-muted" />}</span>
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); if (e.key === 'Escape') onClose(); }}
              placeholder={t('uiKit.status.placeholder', "What's your status?")}
              aria-label={t('uiKit.status.placeholder', "What's your status?")}
              maxLength={128}
              className="flex-1 bg-transparent text-sm text-nord-6 placeholder-nord-muted outline-none"
            />
            {(text || emoji) && (
              <button onClick={() => { setEmoji(''); setText(''); }} aria-label={t('uiKit.status.clearInput', 'Clear')} className="rounded p-0.5 hover:bg-nord-2">
                <X className="h-3.5 w-3.5 text-nord-muted" />
              </button>
            )}
          </div>
        </div>

        {/* Quick Presets */}
        <div className="px-4 pb-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-nord-muted mb-1.5">{t('uiKit.status.quick', 'Quick status')}</p>
          <div className="grid grid-cols-2 gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => handlePreset(p)}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs transition-colors text-left',
                  emoji === p.emoji && text === presetText(t, p)
                    ? 'bg-frost-0/20 text-frost-2'
                    : 'text-nord-4 hover:bg-nord-2 hover:text-nord-6',
                )}
              >
                <span>{p.emoji}</span>
                <span className="truncate">{presetText(t, p)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div className="px-4 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-nord-muted mb-1.5">
            <Clock className="inline h-3 w-3 mr-1" />{t('uiKit.status.clearAfter', 'Clear after')}
          </p>
          <div className="flex flex-wrap gap-1">
            {DURATION_OPTIONS.map((opt, idx) => (
              <button
                key={opt.label}
                onClick={() => setDurationIdx(idx)}
                className={clsx(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  durationIdx === idx
                    ? 'bg-frost-0 text-white'
                    : 'bg-nord-2 text-nord-4 hover:bg-nord-3 hover:text-nord-6',
                )}
              >
                {durationLabel(t, opt)}
              </button>
            ))}
          </div>
        </div>

        {/* Clear on offline option */}
        <div className="px-4 pb-3">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={clearOnOffline}
              onChange={(e) => setClearOnOffline(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-nord-3 bg-nord-0 text-frost-0 focus:ring-frost-0/50 accent-frost-0"
            />
            <span className="text-xs text-nord-4 group-hover:text-nord-6 transition-colors">{t('uiKit.status.clearOnOffline', 'Clear status when I go offline')}</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 border-t border-nord-3 px-4 py-3">
          {currentStatus && (
            <button
              onClick={() => void handleClear()}
              disabled={saving}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-nord-11 hover:bg-nord-11/10 transition-colors disabled:opacity-50"
            >
              {t('uiKit.status.clear', 'Clear status')}
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-xs font-medium text-nord-4 hover:bg-nord-2 transition-colors">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || (!text.trim() && !emoji.trim())}
            className="rounded-lg bg-frost-0 px-4 py-1.5 text-xs font-medium text-white hover:bg-frost-0/80 transition-colors disabled:opacity-50"
          >
            {saving ? t('uiKit.saving', 'Saving…') : t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
